// recharts implementations mirroring the iOS Charts views:
// HourlySalesChart, RevenueTrendChart, DailyRevenueChart, TopItemsChart,
// CategoryPerformanceChart, WeeklyTrendsChart.
// Time-based series (hourly / revenue / daily / weekly) are smooth animated
// area curves; ranked series (top items / categories) stay as bars.
import React, { useId, useState } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { CHART_COLORS, fmtMoney, fmtMoneyShort, formatHourLabel } from './analyticsUtils';
import { SegmentedPicker } from '../../shared/ui';

const AXIS_TICK = { fontSize: 10, fill: '#9CA3AF' };

function CurrencyTooltip({ active, payload, label, prefix = '' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 px-3 py-2 text-xs">
      <p className="font-semibold text-gray-800">{prefix}{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-gray-500">
          {p.name}: <span className="font-bold text-gray-800">
            {p.name === 'Orders' || p.name === 'Qty' ? p.value : fmtMoney(p.value)}
          </span>
        </p>
      ))}
    </div>
  );
}

// ---------- Shared smooth area chart ----------
// Monotone curve + gradient fill, draw-in animation, morphs when live data
// changes, peak point highlighted, pulsing dot on the newest point.
function CurvedAreaChart({
  data: rawData, xKey = 'label', yKey = 'revenue', name = 'Revenue',
  color = '#3B82F6', height = 250, live = true, showAvg = true, tooltip,
  isCurrency = true,
}) {
  const gradId = `grad${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  // Fewer than 3 points can't draw a curve — add zero-value spacers on both
  // sides so a lone point still rises and falls.
  const data = rawData.length > 0 && rawData.length < 3
    ? [{ [xKey]: '', [yKey]: 0, __pad: true }, ...rawData, { [xKey]: '', [yKey]: 0, __pad: true }]
    : rawData;
  const real = data.filter((d) => !d.__pad);
  const avg = real.length ? real.reduce((s, d) => s + (d[yKey] || 0), 0) / real.length : 0;
  const peakIndex = data.reduce(
    (best, d, i) => (!d.__pad && d[yKey] > (data[best]?.[yKey] ?? -1) ? i : best), 0);
  const lastIndex = data.reduce((last, d, i) => (d.__pad ? last : i), 0);
  const axisFmt = isCurrency ? fmtMoneyShort : (v) => `${Math.round(v)}`;
  const tooltipEl = tooltip || <CurrencyTooltip />;
  // Never show a tooltip for the invisible zero spacers
  const safeTooltip = (props) => (
    props?.payload?.[0]?.payload?.__pad ? null : React.cloneElement(tooltipEl, props)
  );

  const renderDot = ({ cx, cy, index }) => {
    if (cx == null || cy == null || data[index]?.__pad) return <g key={index} />;
    if (live && index === lastIndex) {
      return (
        <g key={index}>
          <circle cx={cx} cy={cy} r={4} fill={color} opacity={0.35}>
            <animate attributeName="r" values="4;11;4" dur="1.8s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.45;0;0.45" dur="1.8s" repeatCount="indefinite" />
          </circle>
          <circle cx={cx} cy={cy} r={4} fill={color} stroke="#fff" strokeWidth={1.5} />
        </g>
      );
    }
    if (index === peakIndex && data[index][yKey] > 0) {
      return <circle key={index} cx={cx} cy={cy} r={4} fill="#F97316" stroke="#fff" strokeWidth={1.5} />;
    }
    return <g key={index} />;
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 16, right: 8, left: -14, bottom: 0 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.4} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke="#E5E7EB" strokeDasharray="3 3" />
        <XAxis dataKey={xKey} tick={AXIS_TICK} tickLine={false}
          axisLine={{ stroke: '#E5E7EB' }} interval="preserveStartEnd" />
        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false}
          tickFormatter={axisFmt} domain={[0, (max) => Math.ceil((max || 1) * 1.15)]} />
        <Tooltip content={safeTooltip} cursor={{ stroke: '#9CA3AF', strokeWidth: 1 }} />
        {showAvg && real.length > 1 && (
          <ReferenceLine y={avg} stroke="#9CA3AF" strokeDasharray="4 4"
            label={{ value: `Avg ${axisFmt(avg)}`, position: 'insideTopRight', fontSize: 10, fill: '#6B7280' }} />
        )}
        <Area
          type="monotone" dataKey={yKey} name={name}
          stroke={color} strokeWidth={3} strokeLinecap="round"
          fill={`url(#${gradId})`} dot={renderDot}
          activeDot={{ r: 6, fill: color, stroke: '#fff', strokeWidth: 2 }}
          isAnimationActive animationDuration={900} animationEasing="ease-out"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ---------- Hourly Sales (7am-midnight, smooth curve, peak highlighted, avg line) ----------
export function HourlySalesChart({ data, height = 250 }) {
  // iOS filters to hours >= 7 plus midnight (hour 0), midnight sorted last.
  const filtered = data
    .filter((d) => d.hour >= 7 || d.hour === 0)
    .sort((a, b) => (a.hour === 0 ? 24 : a.hour) - (b.hour === 0 ? 24 : b.hour))
    .map((d) => ({ ...d, label: formatHourLabel(d.hour) }));
  const hasData = filtered.some((d) => d.revenue > 0);

  if (!hasData) return null;
  return <CurvedAreaChart data={filtered} height={height} color={CHART_COLORS[2]} />;
}

// ---------- Revenue Trend (smooth curve, peak highlighted, dashed average) ----------
export function RevenueTrendChart({ data, height = 250 }) {
  if (!data.some((d) => d.revenue > 0)) return null;
  return <CurvedAreaChart data={data} height={height} color="#22C55E" />;
}

// ---------- Daily revenue (iOS DailyRevenueChart / EarningsHistoryChart) ----------
export function DailyRevenueChart({ data, height = 250, color = '#22C55E' }) {
  if (!data.some((d) => d.revenue > 0)) return null;
  return <CurvedAreaChart data={data} height={height} color={color} />;
}

const shortName = (n = '') => (n.length > 10 ? `${n.slice(0, 9)}…` : n);

// Tooltip that shows the full (untruncated) item / category name.
function NamedTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const isQty = payload[0].name === 'Qty' || payload[0].name === 'Orders';
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 px-3 py-2 text-xs">
      <p className="font-semibold text-gray-800">{d.name}</p>
      <p className="text-gray-500">
        {payload[0].name}: <span className="font-bold text-gray-800">
          {isQty ? payload[0].value : fmtMoney(payload[0].value)}
        </span>
      </p>
    </div>
  );
}

// ---------- Top Items (smooth curve, Qty/Rev segmented toggle, top 10) ----------
export function TopItemsChart({ data, height = 280 }) {
  const [showQty, setShowQty] = useState(true);
  const items = data.slice(0, 10).map((d) => ({ ...d, label: shortName(d.name) }));
  return (
    <div>
      <div className="flex justify-end mb-2">
        <div className="w-32">
          <SegmentedPicker
            options={[{ value: 'qty', label: 'Qty' }, { value: 'rev', label: 'Rev' }]}
            value={showQty ? 'qty' : 'rev'}
            onChange={(v) => setShowQty(v === 'qty')}
          />
        </div>
      </div>
      <CurvedAreaChart
        data={items} height={height}
        yKey={showQty ? 'quantity' : 'revenue'} name={showQty ? 'Qty' : 'Revenue'}
        isCurrency={!showQty} color={CHART_COLORS[0]} showAvg={false}
        tooltip={<NamedTooltip />}
      />
    </div>
  );
}

// ---------- Category Performance (smooth curve, Revenue/Orders toggle) ----------
export function CategoryPerformanceChart({ data, height = 280 }) {
  const [metric, setMetric] = useState('revenue');
  const cats = data.slice(0, 10).map((d) => ({ ...d, label: shortName(d.name) }));
  return (
    <div>
      <div className="flex justify-end mb-2">
        <div className="w-44">
          <SegmentedPicker
            options={[{ value: 'revenue', label: 'Revenue' }, { value: 'orders', label: 'Orders' }]}
            value={metric}
            onChange={setMetric}
          />
        </div>
      </div>
      <CurvedAreaChart
        data={cats} height={height}
        yKey={metric} name={metric === 'orders' ? 'Orders' : 'Revenue'}
        isCurrency={metric !== 'orders'} color={CHART_COLORS[4]} showAvg={false}
        tooltip={<NamedTooltip />}
      />
    </div>
  );
}

// ---------- Weekly Trends (smooth revenue curve by weekday + orders tooltip) ----------
export function WeeklyTrendsChart({ data, height = 250 }) {
  const hasData = data.some((d) => d.revenue > 0 || d.orders > 0);
  if (!hasData) return null;
  return (
    <CurvedAreaChart
      data={data} xKey="short" height={height}
      color={CHART_COLORS[3]} showAvg={false}
      tooltip={<WeeklyTooltip />}
    />
  );
}

function WeeklyTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 px-3 py-2 text-xs">
      <p className="font-semibold text-gray-800">{d.week}</p>
      <p className="text-gray-500">Revenue: <span className="font-bold text-gray-800">{fmtMoney(d.revenue)}</span></p>
      <p className="text-gray-500">Orders: <span className="font-bold text-gray-800">{d.orders}</span></p>
    </div>
  );
}
